import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { LayoutDashboard, User, Package, MessageCircle, Plus, Trash2, Edit2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../../../components/ui/ImageUpload';

interface VendorPackage {
    id: string;
    name: string;
    description: string;
    price: number;
    vendor_id: string;
    images: string[];
    is_per_guest?: boolean;
}

export default function VendorPackages() {
    const navigate = useNavigate();
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [packages, setPackages] = useState<VendorPackage[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPackage, setEditingPackage] = useState<VendorPackage | null>(null);
    const [formData, setFormData] = useState({ name: '', description: '', price: '', images: [] as string[], is_per_guest: false });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                navigate('/');
                return;
            }

            setUserId(session.user.id);

            const { data: vendorData } = await supabase
                .from('wedding_vendors')
                .select('id')
                .eq('owner_id', session.user.id)
                .single();

            if (!vendorData) {
                navigate('/vendor/dashboard');
                return;
            }

            setVendorId(vendorData.id);

            const { data: pkgData } = await supabase
                .from('wedding_packages')
                .select('*')
                .eq('vendor_id', vendorData.id)
                .order('price', { ascending: true });

            setPackages((pkgData || []) as VendorPackage[]);
            setLoading(false);
        };

        fetchData();
    }, [navigate]);

    const handleSubmit = async () => {
        if (!vendorId || !formData.name || !formData.price) return;
        setSubmitting(true);

        try {
            if (editingPackage) {
                const { error } = await supabase
                    .from('wedding_packages')
                    .update({
                        name: formData.name,
                        description: formData.description,
                        price: parseFloat(formData.price),
                        images: formData.images,
                        is_per_guest: formData.is_per_guest
                    })
                    .eq('id', editingPackage.id);
                if (error) throw error;
                toast.success('Package updated!');
            } else {
                const { error } = await supabase
                    .from('wedding_packages')
                    .insert({
                        vendor_id: vendorId,
                        name: formData.name,
                        description: formData.description,
                        price: parseFloat(formData.price),
                        images: formData.images,
                        is_per_guest: formData.is_per_guest
                    });
                if (error) throw error;
                toast.success('Package added!');
            }

            // Refresh packages
            const { data: pkgData } = await supabase
                .from('wedding_packages')
                .select('*')
                .eq('vendor_id', vendorId)
                .order('price', { ascending: true });
            setPackages((pkgData || []) as VendorPackage[]);

            setShowModal(false);
            setEditingPackage(null);
            setFormData({ name: '', description: '', price: '', images: [], is_per_guest: false });
        } catch (err: any) {
            toast.error(err.message || 'Failed to save package');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (pkgId: string) => {
        if (!confirm('Delete this package?')) return;

        try {
            const { error } = await supabase
                .from('wedding_packages')
                .delete()
                .eq('id', pkgId);
            if (error) throw error;
            setPackages(packages.filter((p) => p.id !== pkgId));
            toast.success('Package deleted');
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete');
        }
    };

    const openEditModal = (pkg: VendorPackage) => {
        setEditingPackage(pkg);
        setFormData({
            name: pkg.name,
            description: pkg.description || '',
            price: pkg.price.toString(),
            images: pkg.images || [],
            is_per_guest: pkg.is_per_guest || false
        });
        setShowModal(true);
    };

    const menuItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/vendor/dashboard' },
        { icon: User, label: 'Profile', path: '/vendor/profile' },
        { icon: Package, label: 'Packages', path: '/vendor/packages', active: true },
        { icon: MessageCircle, label: 'Inquiries', path: '/vendor/inquiries' },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-500">Loading...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                            <nav className="space-y-1">
                                {menuItems.map((item) => (
                                    <button
                                        key={item.path}
                                        onClick={() => navigate(item.path)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${item.active
                                            ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600'
                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        <item.icon size={20} />
                                        {item.label}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="flex-1">
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Packages</h1>
                            <button
                                onClick={() => {
                                    setEditingPackage(null);
                                    setFormData({ name: '', description: '', price: '', images: [], is_per_guest: false });
                                    setShowModal(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors"
                            >
                                <Plus size={18} />
                                Add Package
                            </button>
                        </div>

                        {packages.length === 0 ? (
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center border border-gray-200 dark:border-gray-700">
                                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400 mb-4">No packages yet. Add your first package to showcase your offerings.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {packages.map((pkg) => (
                                    <div
                                        key={pkg.id}
                                        className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{pkg.name}</h3>
                                            <div className="flex flex-col items-end">
                                                <span className="text-rose-600 font-bold text-lg">₹{pkg.price.toLocaleString()}</span>
                                                {pkg.is_per_guest && (
                                                    <span className="text-xs text-gray-500 font-medium bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">per guest</span>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{pkg.description || 'No description'}</p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openEditModal(pkg)}
                                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-rose-600 transition-colors"
                                            >
                                                <Edit2 size={14} />
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(pkg.id)}
                                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                {editingPackage ? 'Edit Package' : 'Add Package'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., Basic Package"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Price (₹) *</label>
                                <input
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., 50000"
                                />
                            </div>

                            <div className="flex items-center gap-2 py-1">
                                <input
                                    type="checkbox"
                                    id="is_per_guest"
                                    checked={formData.is_per_guest}
                                    onChange={(e) => setFormData({ ...formData, is_per_guest: e.target.checked })}
                                    className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                                />
                                <label htmlFor="is_per_guest" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Price is per guest/plate?
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="Describe what's included..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Package Images</label>
                                {userId && vendorId && (
                                    <ImageUpload
                                        bucket="vendor-images"
                                        path={`${userId}/packages/${vendorId}`}
                                        currentImages={formData.images}
                                        onUpload={(urls) => setFormData({ ...formData, images: urls })}
                                        maxFiles={5}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowModal(false)}
                                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting || !formData.name || !formData.price}
                                className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                            >
                                {submitting ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
